import React from 'react';
import { Alert, Button, Modal, Spin, Table, Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import {
  AzureOnboardingAccessCheck,
  AzureOnboardingVerifyResult,
  AzureScope,
} from '../../../../services/azure/azureOnboarding/azureOnboarding.model';
import { getStatusDisplay, scopeStatusColor, scopeTypeLabel } from '../azureOnboarding.utils';
import { CoveredButActiveAlert } from './ScopeRefList';

const { Text } = Typography;

export interface VerifyState {
  title: string;
  loading: boolean;
  result?: AzureOnboardingVerifyResult;
  error?: string;
  scopes: AzureScope[];
}

type TestedScope = NonNullable<AzureOnboardingVerifyResult['scopes']>[number];

const ERROR_TEXT = 'Verification failed unexpectedly - see the server log.';

// Friendly names for the access checks verify runs (display only).
const ACCESS_LABELS: Record<string, string> = {
  billing_account: 'Billing account reader / EnrollmentReader',
  subscription: 'Cost Management Reader (subscription)',
  scope: 'Read access (scope)',
  reservations_reader: 'Reservations Reader',
  savings_plan_reader: 'Savings plan Reader',
};

const accessColumns: ColumnsType<AzureOnboardingAccessCheck> = [
  {
    title: 'Access',
    dataIndex: 'check',
    key: 'check',
    render: (val: string) => ACCESS_LABELS[val] || val,
  },
  {
    title: 'Scope',
    dataIndex: 'scope',
    key: 'scope',
    render: (val?: string) => (val ? <Text code>{val}</Text> : 'Tenant-wide'),
  },
  {
    title: 'Result',
    key: 'ok',
    render: (_, record) =>
      record.ok === true ? (
        <Tag color="green">Granted</Tag>
      ) : record.ok === false ? (
        <Tag color="red">Missing</Tag>
      ) : (
        <Tag>Not tested</Tag>
      ),
  },
  {
    title: 'Details',
    key: 'error',
    render: (_, record) => (
      <Text type="secondary" style={{ wordBreak: 'break-word' }}>
        {record.ok === false
          ? record.error || record.status || ''
          : record.ok === null
          ? 'Azure throttled the check - verify again later.'
          : ''}
      </Text>
    ),
  },
];

const VerifyResultModal: React.FC<{ state: VerifyState; onClose: () => void }> = ({
  state,
  onClose,
}) => {
  const { result, scopes } = state;
  const display = result ? getStatusDisplay(result.status) : null;

  const columns: ColumnsType<TestedScope> = [
    {
      title: 'Type',
      dataIndex: 'scope_type',
      key: 'scope_type',
      render: (val: string) => scopeTypeLabel(val),
    },
    {
      title: 'Scope id',
      dataIndex: 'scope_id',
      key: 'scope_id',
      render: (val: string) => <Text code>{val}</Text>,
    },
    {
      title: 'Display name',
      key: 'display_name',
      render: (_, record) => scopes.find((s) => s.id === record.id)?.display_name || '–',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (val: string) =>
        val === 'NotTested' ? (
          <Tag>Not tested: Azure throttled the check.</Tag>
        ) : (
          <Tag color={scopeStatusColor[val] || 'default'}>{val}</Tag>
        ),
    },
  ];

  return (
    <Modal
      wrapClassName="custom-modal"
      title={state.title}
      centered
      visible
      onCancel={onClose}
      footer={null}
      width={820}
    >
      <Spin spinning={state.loading}>
        {state.error && <Alert type="error" showIcon message={state.error} />}
        {result && display && (
          <>
            <div className="form-group">
              Status <Tag color={display.color}>{display.label}</Tag>
              {result.status === 'Error' ? (
                <div>
                  <Text type="danger">{ERROR_TEXT}</Text>
                </div>
              ) : (
                display.tooltip && <div>{display.tooltip}</div>
              )}
              {result.error && result.error !== ERROR_TEXT && (
                <div>
                  <Text type="secondary" style={{ wordBreak: 'break-word' }}>
                    {result.error}
                  </Text>
                </div>
              )}
            </div>
            {result.scopes && result.scopes.length > 0 && (
              <>
                <Table<TestedScope>
                  rowKey="id"
                  size="small"
                  columns={columns}
                  dataSource={result.scopes}
                  pagination={false}
                />
                <Text type="secondary">Scopes not listed weren&apos;t tested in this run.</Text>
              </>
            )}
            {result.access && result.access.length > 0 && (
              <div className="form-group" style={{ marginTop: 16 }}>
                <Text strong>
                  Access check (Synapse 360 Azure&apos;s permissions in this tenant)
                </Text>
                <Table<AzureOnboardingAccessCheck>
                  rowKey={(record) => `${record.check}|${record.scope || ''}`}
                  size="small"
                  columns={accessColumns}
                  dataSource={result.access}
                  pagination={false}
                />
              </div>
            )}
            <CoveredButActiveAlert refs={result.covered_but_active} scopes={scopes} />
          </>
        )}
      </Spin>
      <div className="btns-block modal-footer">
        <Button type="primary" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
};

export default VerifyResultModal;
